import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { BondWizard } from './BondWizard';
import { FINANCIAL_PRODUCTS } from '../config';
import { isAddress } from 'viem';
import { ProductDetails, EntityDetails } from '../contracts/trinity';

interface FinancialProduct {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  enabled: boolean;
  address?: `0x${string}`;
}

interface SelectProductProps {
  entity: EntityDetails;
  onCancel: () => void;
  onSuccess: (productAddress: `0x${string}`) => void;
}

export function SelectProduct({ entity, onCancel, onSuccess }: SelectProductProps) {
  const [showBondWizard, setShowBondWizard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  console.log('Financial Products:', FINANCIAL_PRODUCTS); // Debug log
  console.log('Entity Address:', entity.address); // Debug log

  const handleProductSelect = (product: FinancialProduct) => {
    if (product.enabled) {
      if (product.id === 'bonds') {
        // Validate entity address
        if (!entity.address) {
          setError('No entity selected. Please select an entity first.');
          return;
        }
        if (!isAddress(entity.address)) {
          setError('Invalid entity address. Please select a valid entity.');
          return;
        }
        setError(null);
        setShowBondWizard(true);
      } else if (product.address) {
        onSuccess(product.address);
      }
    }
  };

  return (
    <div className="p-10 bg-black/10  rounded-2xl border border-white/10 shadow-2xl">
      <div className="flex justify-between items-center mb-6">
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-8">
        
        {FINANCIAL_PRODUCTS.map((product) => (
          <motion.button
            key={product.id}
            onClick={() => handleProductSelect(product)}
            disabled={!product.enabled}
            className={`relative aspect-square flex items-center justify-center rounded-3xl transition-all duration-300 ${
              product.enabled
                ? 'bg-gradient-to-br from-blue-500/70 via-purple-500/70 to-pink-500/80 hover:from-blue-500/80 hover:via-purple-500/80 hover:to-pink-500/90'
                : 'bg-gray-900/80 cursor-not-allowed backdrop-blur-sm'
            }`}
            whileHover={product.enabled ? { scale: 1.05 } : undefined}
            whileTap={product.enabled ? { scale: 0.95 } : undefined}
          >
            <div className={`absolute inset-0 rounded-3xl ${
              product.enabled 
                ? 'bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10' 
                : ''
            }`} />
            <product.icon
              size={80}
              weight="regular"
              className={product.enabled ? 'text-white' : 'text-gray-600'}
            />
            <div className="absolute -bottom-6 left-0 right-0 text-center">
              <span className={`text-sm font-medium ${product.enabled ? 'text-white/80' : 'text-gray-500'}`}>
                {product.name}
              </span>
            </div>
            {!product.enabled && (
              <div className="absolute inset-0 flex items-center justify-center rounded-3xl bg-black/40" />
            )}
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {showBondWizard && entity.address && isAddress(entity.address) && (
          <BondWizard 
            onClose={() => {
              setShowBondWizard(false);
              onCancel();
            }} 
            onSuccess={(productAddress) => {
              // Only close and call onSuccess when the user clicks "Complete Setup"
              onSuccess(productAddress);
            }}
            entityAddress={entity.address}
          />
        )}
      </AnimatePresence>

    </div>
  );
} 